import { Component, computed, inject, OnInit, signal } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";

import { AdaaHelper } from "../../../../core/utils";
import { noWhitespaceValidator } from "../../../../core/validators";
import { Constants } from "../../../constants/constants";
import { CountryValuesTypes, FileInputType, PageMode } from "../../../constants/enums";
import { AttachFile, IntpCountryValueModel, IntpCountryValueRowModel, ValueText } from "../../../models";
import {
  FormDropdownComponent,
  FormInputComponent,
  FormInputDateComponent,
  FormInputDateRangeComponent,
  FormInputFileComponent,
} from "../../form";

@Component({
  selector: "adaa-intp-country-value-modal",
  imports: [
    TranslatePipe,
    ReactiveFormsModule,
    FormInputComponent,
    FormInputFileComponent,
    FormDropdownComponent,
    FormInputDateComponent,
    FormInputDateRangeComponent,
  ],
  templateUrl: "./intp-country-value-modal.html",
  styleUrl: "./intp-country-value-modal.scss",
})
export class IntpCountryValueModalComponent implements OnInit {
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _toastrService = inject(ToastrService);
  private readonly _translateService = inject(TranslateService);
  readonly modal = inject(NgbActiveModal);

  //Inputs
  pageMode = signal<PageMode>(PageMode.create);
  title = signal<string>("");
  isUaeAndCountry = signal<boolean>(false);
  intpCountryValue = signal<IntpCountryValueModel | undefined>(undefined);
  valueTypeOptions = signal<ValueText[]>([]);

  //Props
  CountryValuesTypes = CountryValuesTypes;
  model: FormGroup;
  inNumberValues: IntpCountryValueRowModel[] = [];
  submitted = false;
  allowedFiles: FileInputType[] = [
    FileInputType.bmp,
    FileInputType.jpeg,
    FileInputType.jpg,
    FileInputType.png,
    FileInputType.svg,
  ];

  isViewMode = computed(() => this.pageMode() === PageMode.view);

  public get getImage(): AttachFile {
    const logo = this.model.get("iconId")?.value;
    const filename = this.model.get("filename")?.value;

    return {
      id: logo,
      filename: filename,
    };
  }

  public get displayLabel(): string {
    const titleEN = this.model.get("titleEN")?.value;
    const titleAE = this.model.get("titleAE")?.value;

    return AdaaHelper.getPageLabel(titleAE, titleEN);
  }

  public get typeId(): number {
    return this.model.get("typeId")?.value;
  }

  public ngOnInit(): void {
    this.model = this._prepareForm();

    const intpCountryValue = this.intpCountryValue();
    if (intpCountryValue) {
      this.model.patchValue(intpCountryValue);
      this.model
        .get("filename")
        ?.setValue(
          AdaaHelper.isDefinedAndNotEmpty(intpCountryValue.filename)
            ? intpCountryValue.filename
            : intpCountryValue.icon?.filename
        );
      this.inNumberValues = intpCountryValue.inNumberValues ?? [];
    }
  }

  public onFilechange(event: AttachFile | null) {
    this.model.get("iconId")?.setValue(event?.id);
    this.model.get("filename")?.setValue(event?.filename);
  }

  public typeIdChanged(value: number) {
    //If no data found add a new record
    if (this.inNumberValues.length === 0) {
      this.inNumberValues.push({
        fakeId: Math.floor(Math.random() * 217 * 1000),
        status: Constants.OBJECT_STATUS.ACTIVE,
        valueType: value,
      });
    }

    //reset values on ddl change
    this.inNumberValues.forEach((e) => {
      e.endDate = undefined;
      e.keyDate = undefined;
      e.keyEndDate = undefined;
      e.keyStartDate = undefined;
      e.keyText = undefined;
      e.startDate = undefined;
      e.valueDate = undefined;
      e.valueNumber = undefined;
      e.valueText = undefined;
      e.valueType = value;
    });

    // Remove all the extra items and keep the first one
    if (this.inNumberValues.length > 1) this.inNumberValues = this.inNumberValues.slice(0, 1);

    const type = this.valueTypeOptions().find((e) => e.value === this.typeId);
    if (type) {
      this.model.get("typeNameAE")?.setValue(type.text);
      this.model.get("typeNameEN")?.setValue(type.text);
    }
  }

  public inNumberValueChanged(
    item: IntpCountryValueRowModel,
    value: string | undefined,
    fieldName:
      | "valueText"
      | "valueNumber"
      | "valueDate"
      | "dateRange"
      | "dateRangeKey"
      | "keyDate"
      | "keyText"
      | "valueTextAE"
      | "valueTextEN"
      | "keyTextAE"
      | "keyTextEN"
  ) {
    if (fieldName === "dateRange") {
      const valuesArr = value?.split("-");
      if (!valuesArr) return;

      const startDate = valuesArr[0] ? valuesArr[0].replace(" ", "") : undefined;
      const endDate = valuesArr[1] ? valuesArr[1].replace(" ", "") : undefined;

      item.startDate = startDate && startDate !== "null" ? startDate : undefined;
      item.endDate = endDate && endDate !== "null" ? endDate : undefined;
    } else if (fieldName === "dateRangeKey") {
      const valuesArr = value?.split("-");
      if (!valuesArr) return;

      const startDate = valuesArr[0] ? valuesArr[0].replace(" ", "") : undefined;
      const endDate = valuesArr[1] ? valuesArr[1].replace(" ", "") : undefined;

      item.keyStartDate = startDate && startDate !== "null" ? startDate : undefined;
      item.keyEndDate = endDate && endDate !== "null" ? endDate : undefined;
    } else {
      item[fieldName] = value;
    }

    const index = this.inNumberValues.findIndex((e) => (item.fakeId ? item.fakeId === e.fakeId : item.id === e.id));
    if (index > -1) this.inNumberValues.splice(index, 1, item);
  }

  public addItem() {
    this.inNumberValues.push({
      fakeId: Math.floor(Math.random() * 217 * 1000),
      status: Constants.OBJECT_STATUS.ACTIVE,
      valueType: this.typeId,
    });
  }

  public removeItem(item: IntpCountryValueRowModel) {
    const index = this.inNumberValues.findIndex((e) => (item.fakeId ? item.fakeId === e.fakeId : item.id === e.id));
    if (index > -1) this.inNumberValues.splice(index, 1);
  }

  public save() {
    this.submitted = true;

    this.model.get("inNumberValues")?.setValue(this.inNumberValues);

    if (!this.model.valid || this._validateData()) {
      this._toastrService.warning(this._translateService.instant("notification.warning.missing_info"));
      return;
    }

    this.modal.close(this.model.getRawValue());
  }

  private _prepareForm() {
    const form: FormGroup = this._formBuilder.group({
      id: null,
      fakeId: null,
      countryId: null,
      titleAE: [{ value: null, disabled: this.isViewMode() }, [Validators.required, noWhitespaceValidator()]],
      titleEN: [{ value: null, disabled: this.isViewMode() }, [Validators.required, noWhitespaceValidator()]],
      dscAE: { value: null, disabled: this.isViewMode() },
      dscEN: { value: null, disabled: this.isViewMode() },
      iconId: { value: null, disabled: this.isViewMode() },
      filename: null,
      typeId: [{ value: null, disabled: this.isViewMode() }, Validators.required],
      typeNameAE: null,
      typeNameEN: null,
      updateUser: null,
      uaeCountryInNumbers: this.isUaeAndCountry(),
      inNumberValues: [],
      status: null,
    });

    if (!this.isUaeAndCountry()) {
      form.addControl(
        "dataSourceAE",
        new FormControl({
          value: null,
          disabled: this.isViewMode(),
        })
      );

      form.addControl(
        "dataSourceEN",
        new FormControl({
          value: null,
          disabled: this.isViewMode(),
        })
      );
    }

    return form;
  }

  private _validateData() {
    switch (this.typeId) {
      case CountryValuesTypes.TEXT:
        return this.inNumberValues.some(
          (e) => !AdaaHelper.isDefinedAndNotEmpty(e.valueTextEN) || !AdaaHelper.isDefinedAndNotEmpty(e.valueTextAE)
        );
      case CountryValuesTypes.NUMBER:
        return this.inNumberValues.some((e) => !AdaaHelper.isDefinedAndNotEmpty(e.valueNumber));
      case CountryValuesTypes.DATE:
        return this.inNumberValues.some((e) => !AdaaHelper.isDefinedAndNotEmpty(e.valueDate));
      case CountryValuesTypes.DATE_RANGE:
        return this.inNumberValues.some(
          (e) => !AdaaHelper.isDefinedAndNotEmpty(e.startDate) || !AdaaHelper.isDefinedAndNotEmpty(e.endDate)
        );
      case CountryValuesTypes.DATE_RANGE_VALUE:
        return this.inNumberValues.some(
          (e) =>
            !AdaaHelper.isDefinedAndNotEmpty(e.keyStartDate) ||
            !AdaaHelper.isDefinedAndNotEmpty(e.keyEndDate) ||
            !AdaaHelper.isDefinedAndNotEmpty(e.valueText)
        );
      case CountryValuesTypes.KEY_VALUE:
        return this.inNumberValues.some(
          (e) =>
            !AdaaHelper.isDefinedAndNotEmpty(e.keyTextAE) ||
            !AdaaHelper.isDefinedAndNotEmpty(e.valueTextAE) ||
            !AdaaHelper.isDefinedAndNotEmpty(e.keyTextEN) ||
            !AdaaHelper.isDefinedAndNotEmpty(e.valueTextEN)
        );
      case CountryValuesTypes.LIST_PER_DATE:
        return this.inNumberValues.some(
          (e) => !AdaaHelper.isDefinedAndNotEmpty(e.keyDate) || !AdaaHelper.isDefinedAndNotEmpty(e.valueText)
        );
      default:
        return false;
    }
  }
}
